import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Gift } from "@phosphor-icons/react";
import { GiftBadge } from "@/components/souvenirs/GiveToMembers";
import { toast } from "sonner";

function GiftItem({ gift, onChange }) {
  const [reporting, setReporting] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const respond = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/me/souvenirs/${gift.id}/respond`, { action, note: action === "problem" ? note : "" });
      toast.success(action === "received" ? "Thank you. Receipt confirmed." : "Problem reported to your representative");
      setReporting(false); onChange(data);
      window.dispatchEvent(new Event("souvenirs-updated"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const items = [gift.tshirts ? `T-shirt${gift.tshirt_size ? ` (size ${gift.tshirt_size})` : ""}` : "", gift.cloth ? "Cloth" : ""].filter(Boolean).join(" + ");
  return (
    <div className="rounded-md border border-border p-4" data-testid={`my-souvenir-${gift.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium" data-testid="my-souvenir-items">{items}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Given by {gift.given_by_name} · {gift.pool_label} · {new Date(gift.updated_at || gift.given_at).toLocaleDateString()}</p>
          {gift.note && <p className="text-xs text-muted-foreground mt-1">Note: {gift.note}</p>}
          {gift.status === "problem" && <p className="text-xs text-destructive mt-1">You reported: {gift.member_note}</p>}
        </div>
        <GiftBadge status={gift.status} testId="my-souvenir-status" />
      </div>
      {gift.status !== "received" && !reporting && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => respond("received")} disabled={busy} data-testid="my-souvenir-confirm">Confirm received</Button>
          {gift.status !== "problem" && <Button size="sm" variant="outline" onClick={() => setReporting(true)} data-testid="my-souvenir-report">Report a problem</Button>}
        </div>
      )}
      {reporting && (
        <div className="mt-3 space-y-2">
          <Textarea rows={2} maxLength={500} placeholder="What is wrong? e.g. not received, wrong size" value={note} onChange={(e) => setNote(e.target.value)} data-testid="my-souvenir-problem-note" />
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" onClick={() => respond("problem")} disabled={busy || !note.trim()} data-testid="my-souvenir-problem-submit">Send report</Button>
            <Button size="sm" variant="ghost" onClick={() => setReporting(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function MySouvenirs({ standalone = false }) {
  const [gifts, setGifts] = useState(null);
  useEffect(() => { api.get("/me/souvenirs").then((r) => setGifts(r.data)).catch(() => setGifts([])); }, []);
  if (standalone && gifts && !gifts.length) {
    return (
      <Card className="mt-6 p-8 text-center" data-testid="my-souvenirs-empty">
        <Gift size={32} weight="bold" className="mx-auto text-muted-foreground" />
        <p className="mt-3 font-medium">No souvenirs have been allocated to you yet</p>
        <p className="text-sm text-muted-foreground mt-1">When your Facility Representative or Chapter Secretary records souvenirs for you, they will appear here for you to confirm.</p>
      </Card>
    );
  }
  if (!gifts?.length) return null;
  return (
    <Card className="mt-6 p-6 border-t-4 border-t-success" data-testid="my-souvenirs">
      <div className="flex items-center gap-2"><Gift size={20} weight="bold" className="text-primary" /><h2 className="font-display text-lg font-bold">My Souvenirs</h2></div>
      <p className="text-sm text-muted-foreground mt-1">Souvenirs recorded as given to you. Please confirm when you have them.</p>
      <div className="mt-4 space-y-3">
        {gifts.map((g) => <GiftItem key={g.id} gift={g} onChange={(n) => setGifts(gifts.map((x) => (x.id === n.id ? n : x)))} />)}
      </div>
    </Card>
  );
}
